import { FieldConfig } from '@lucidtech/las-sdk-browser';
import { DeepKeys, FieldApi } from '@tanstack/react-form';
import type { ColumnDef } from '@tanstack/react-table';
import { memo, useCallback, useMemo } from 'react';

import { merge } from '@/components';
import { Button, Flex, Heading, Save, Table2, Text } from '@/components/core';
import { AddIcon, CloseIcon, InsertRowAboveIcon, InsertRowBelowIcon, TableFieldIcon } from '@/components/icons';
import { Field, FieldLabel } from '@/components/ui';
import { MULTI_VALUE_COLUMN_NAME } from '@/constants';
import { useAutoSaveColumnSizes, useFormContext } from '@/hooks';
import { FormValueRow, selectFromFieldConfig, useDocumentStore, useValidationStore } from '@/store';
import { generateId, toString } from '@/utils';

const getDefaultColumnSize = (config?: FieldConfig | null, parentContainerWidth?: number): number => {
  const numFields = Object.keys(config ?? {}).length;
  return Math.max(parentContainerWidth && numFields > 0 ? parentContainerWidth / numFields - 16 * 2 : 0, 200);
};

export type TableFieldProps = {
  name: string;
  rowId: string;
};

const ITableField = ({ name, rowId }: TableFieldProps) => {
  const form = useFormContext();
  const config = useDocumentStore(selectFromFieldConfig(name));
  const status = useDocumentStore((state) => state.status);

  const hideTableView = useValidationStore((state) => state.hideTableView);
  const parentContainerWidth = useValidationStore((state) => state.parentContainerWidth);

  const onMinimize = useCallback(() => {
    hideTableView(true);
  }, [hideTableView]);

  const defaultColumnSize = getDefaultColumnSize(config?.fields, parentContainerWidth);
  const { tableColumnSizes } = useAutoSaveColumnSizes();

  const columns = useMemo<ColumnDef<FormValueRow>[]>(() => {
    if (config?.type === 'table') {
      const rowNumberColumn = {
        accessorKey: 'index',
        header: () => (
          <div className="w-10">
            <Heading size="sm" className="my-2 text-center text-gray-400">
              {'#'}
            </Heading>
          </div>
        ),
        cell: ({ row }) => (
          <div className="flex w-10 items-center justify-center">
            <Text className="text-gray-400">{row.index + 1}</Text>
          </div>
        ),
        size: 40,
      };

      const fieldColumns = Object.entries(config?.fields ?? {}).map(([columnName, columnConfig]) => ({
        accessorKey: `columns.${columnName}`,
        header: () => (
          <Heading size="sm" className="px-4 py-2">
            {columnConfig.name}
          </Heading>
        ),
        cell: ({ row }) => (
          <Field
            rowId={row.id}
            hideLabel
            name={`${name}[${row.index}].columns.${columnName}`}
            isTableField
            isMultiLine={true}
            className="p-xs"
          />
        ),
        size: tableColumnSizes[`columns_${columnName}`] || defaultColumnSize,
      }));

      return [rowNumberColumn, ...fieldColumns];
    } else {
      return [
        {
          accessorKey: `columns.${MULTI_VALUE_COLUMN_NAME}`,
          header: MULTI_VALUE_COLUMN_NAME,
          cell: ({ row }) => (
            <Field rowId={row.id} hideLabel name={`${name}[${row.index}].columns.${MULTI_VALUE_COLUMN_NAME}`} />
          ),
          size: Number.MAX_SAFE_INTEGER,
        },
      ];
    }
  }, [config?.type, config?.fields, tableColumnSizes, name, defaultColumnSize]);

  const createRow = useCallback(() => {
    const row: FormValueRow = { id: generateId(), columns: {} };
    for (const fieldName of Object.keys(config?.fields ?? {})) {
      row.columns[fieldName] = {
        current: { value: '' },
        candidates: [],
      };
    }
    return row;
  }, [config?.fields]);

  const createValue = useCallback((): FormValueRow => {
    return {
      id: generateId(),
      columns: {
        [MULTI_VALUE_COLUMN_NAME]: {
          current: { value: '' },
          candidates: [],
        },
      },
    };
  }, []);

  const createCopy = useCallback((value: FormValueRow) => {
    return { ...structuredClone(value), id: generateId() };
  }, []);

  const addNewValueOrRow = useCallback(
    async (field: FieldApi) => {
      if (config?.type === 'multi-value') {
        field.pushValue(createValue());
      } else if (config?.type === 'table') {
        await field.insertValue(field?.state?.value?.length ?? 0, createRow());
      }
    },
    [config?.type, createRow, createValue]
  );

  return (
    <form.Field key={rowId} name={name} mode="array">
      {(field) => {
        if (!field.state.value) return <Save message="Loading..." />;

        return (
          <div className="py-md gap-md bg-gray-25 flex w-full flex-col px-[2rem]">
            <div className="gap-sm flex items-center">
              <TableFieldIcon />
              <FieldLabel name={config?.name || name} description={toString(config?.description)} />

              <div className="grow" />

              <Button excludeFromTabOrder variant="plain" onPress={onMinimize}>
                <CloseIcon />
              </Button>
            </div>

            <Flex col className="h-full w-full">
              <Table2
                className="border-separate border-spacing-0 rounded-lg border-1 border-t-0 border-gray-200 bg-gray-50 text-left"
                props={{
                  header: {
                    className: merge('bg-gray-50 border-gray-200 rounded-lg border-t-1'),
                  },
                  headerCell: {
                    className: merge('border-gray-200 not-aria-[colindex=0]:border-l-1'),
                  },
                  row: {
                    className: 'border-t-1 border-gray-200',
                  },
                  cell: {
                    className: merge(
                      'border-gray-200 not-aria-[colindex=0]:bg-white not-aria-[colindex=0]:border-l-1',
                      status !== 'ready-for-review' && 'bg-gray-50'
                    ),
                  },
                  container: {
                    className: 'pl-md',
                  },
                }}
                data={field.state.value as FormValueRow[]}
                columns={columns}
                isInnerTable={false}
                hideHeader={config?.type === 'multi-value'}
                hideDragHandle={status !== 'ready-for-review'}
                hideRowOptions={status !== 'ready-for-review'}
                isResizeable
                onRowIndexChange={(oldIndex, newIndex) => {
                  field.moveValue(oldIndex, newIndex);
                  for (const columnName of Object.keys(config?.fields ?? {})) {
                    const oldFieldName = `${name}[${oldIndex}].columns.${columnName}`;
                    form.validateField(oldFieldName as DeepKeys<FormValueRow>, 'change');
                    const newFieldName = `${name}[${newIndex}].columns.${columnName}`;
                    form.validateField(newFieldName as DeepKeys<FormValueRow>, 'change');
                  }
                }}
                duplicateRowAction={async (row) => {
                  await field.insertValue(row.index + 1, createCopy(row.original));
                }}
                deleteRowAction={async (row) => {
                  await field.removeValue(row.index);
                }}
                options={[
                  {
                    onAction: async (row) => {
                      await field.insertValue(row.index, createRow());
                    },
                    children: (
                      <>
                        <InsertRowAboveIcon />
                        <Text>Insert row above</Text>
                      </>
                    ),
                  },
                  {
                    onAction: async (row) => {
                      await field.insertValue(row.index + 1, createRow());
                    },
                    children: (
                      <>
                        <InsertRowBelowIcon />
                        <Text>Insert row below</Text>
                      </>
                    ),
                  },
                ]}
              />
            </Flex>
            {status === 'ready-for-review' && config?.type && ['table', 'multi-value'].includes(config.type) ? (
              <Button className="w-fit" excludeFromTabOrder variant="plain" onPress={() => addNewValueOrRow(field)}>
                <AddIcon />
                <Text>{config?.type === 'multi-value' ? 'Add value' : 'Add row'}</Text>
              </Button>
            ) : null}
          </div>
        );
      }}
    </form.Field>
  );
};

export const TableField = memo(ITableField) as typeof ITableField;
